import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { resetPushSetup } from "../lib/push";
import { useCrew } from "../lib/crew";
import { JoinByLink } from "../components/JoinByLink";
import { useTheme, type ThemeChoice } from "../lib/theme";
import { supabase } from "../lib/supabase";
import { Avatar } from "../components/Avatar";
import { PronounPicker } from "../components/PronounPicker";
import { myApprovedCount } from "../lib/league";
import { personName } from "../shared/format";

// You: who you are, the crews you are in, and your settings.
//
// The tab exists mostly so the other three can stay simple. Anything that is
// about the person rather than the game lives here, which keeps Home about
// what is happening now.
export function You() {
  const { user, signOut } = useAuth();
  const { crew, crews, isHost, crewId } = useCrew();
  const [joining, setJoining] = useState(false);
  const { choice, setChoice } = useTheme();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [savedName, setSavedName] = useState("");
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState<number | null>(null);
  const [waiting, setWaiting] = useState(0);
  const [pronouns, setPronouns] = useState<string | null>(null);
  const [loadedMe, setLoadedMe] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!user) return;
      const { data } = await supabase.from("users").select("display_name, pronouns").eq("id", user.id).maybeSingle();
      if (!alive) return;
      const n = personName(data?.display_name, "");
      setName(n);
      setSavedName(n);
      setPronouns(data?.pronouns ?? null);
      setLoadedMe(true);

      const { data: season } = await supabase.from("seasons").select("id").eq("league_id", crewId).maybeSingle();
      if (season?.id && alive) {
        setReady(await myApprovedCount(season.id, user.id));
        // Anything still needing their yes is the one thing here that is
        // actually asking something of them, so it gets said out loud.
        const { count } = await supabase
          .from("question_candidates")
          .select("id", { count: "exact", head: true })
          .eq("season_id", season.id).eq("owner_user_id", user.id).eq("status", "pending");
        if (alive) setWaiting(count ?? 0);
      }
    })();
    return () => { alive = false; };
  }, [user, crewId]);

  async function save() {
    if (!user || !name.trim()) return;
    setSaving(true);
    await supabase.from("users").update({ display_name: name.trim() }).eq("id", user.id);
    setSavedName(name.trim());
    setSaving(false);
  }

  const modes: { key: ThemeChoice; label: string }[] = [
    { key: "light", label: "Light" },
    { key: "dark", label: "Dark" },
    { key: "system", label: "Match my phone" },
  ];

  return (
    <div className="app">
      <div className="row" style={{ gap: 14, alignItems: "center" }}>
        <Avatar id={user?.id ?? ""} name={savedName || "You"} size={56} />
        <div style={{ minWidth: 0 }}>
          <h1 style={{ fontSize: 22 }}>{savedName || "Add your name"}</h1>
          <p className="muted" style={{ fontSize: 13, overflowWrap: "anywhere" }}>{user?.email}</p>
        </div>
      </div>

      <div className="card">
        <span className="tag">Your name</span>
        <p className="muted">This is what the crew sees next to your answers.</p>
        <input
          className="input"
          value={name}
          placeholder="Your name"
          onChange={(e) => setName(e.target.value)}
        />
        {/* Only offer the button when there is something to save. A permanently
            greyed-out "Saved" is the loudest thing in the card and cannot be
            pressed, which reads as broken rather than as finished. */}
        {name.trim() !== savedName ? (
          <button className="btn primary" disabled={saving || !name.trim()} onClick={save}>
            {saving ? "Saving…" : "Save name"}
          </button>
        ) : (
          savedName && <p className="muted saved-note">Saved</p>
        )}
      </div>

      {/* The spec is explicit that this must be obvious rather than buried, so
          it sits directly under the profile with the count as the headline and
          a primary button, not a link at the bottom of a settings list. */}
      <div className="card">
        <span className="tag">Your questions</span>
        {ready == null ? (
          <p className="muted">Counting…</p>
        ) : (
          <p className="q-count">
            <b>{ready}</b> ready to play about you
          </p>
        )}
        {waiting > 0 && (
          <p className="muted">
            {waiting} {waiting === 1 ? "is" : "are"} still waiting on your yes.
          </p>
        )}
        <p className="muted">
          Questions come from your updates, and nothing is playable until you approve it.
        </p>
        <button className="btn primary" onClick={() => navigate(`/league/${crewId}/questions`)}>
          See your questions
        </button>
        <button className="btn ghost" onClick={() => navigate(`/league/${crewId}/checkin`)}>
          Add an update
        </button>
      </div>

      {loadedMe && user && (
        <PronounPicker userId={user.id} current={pronouns} onDone={setPronouns} compact />
      )}

      <div className="card">
        <span className="tag">Your crews</span>
        {crews.map((c) => (
          <button
            key={c.id}
            className={`sheet-row${c.id === crew?.id ? " current" : ""}`}
            onClick={() => { if (c.id !== crew?.id) window.location.href = `/league/${c.id}`; }}
          >
            <span className="sheet-row-name">{c.name}</span>
            <span className="sheet-row-meta">{c.role === "commissioner" ? "You host this one" : ""}</span>
            {c.id === crew?.id && <span className="sheet-row-check" aria-label="Current crew">✓</span>}
          </button>
        ))}
        {joining ? (
          <JoinByLink label="Paste a crew link" cta="Join that crew" />
        ) : (
          <button className="btn ghost" onClick={() => setJoining(true)}>Join another crew</button>
        )}
        <button className="btn ghost" onClick={() => navigate("/new-league")}>Start your own crew</button>
      </div>

      <div className="card">
        <span className="tag">Appearance</span>
        <div className="seg" role="group" aria-label="Appearance">
          {modes.map((m) => (
            <button
              key={m.key}
              className={`seg-btn${choice === m.key ? " current" : ""}`}
              aria-pressed={choice === m.key}
              onClick={() => setChoice(m.key)}
            >
              {m.label}
            </button>
          ))}
        </div>
        <p className="muted">Results always show in light, so the reveal still lands.</p>
      </div>

      {isHost && (
        <div className="card">
          <span className="tag">You host this crew</span>
          <p className="muted">Season settings, invites, and drops live here.</p>
          <button className="btn ghost" onClick={() => navigate(`/league/${crewId}/commissioner`)}>
            Open host tools
          </button>
        </div>
      )}

      <div className="card">
        <span className="tag">Notifications</span>
        <p className="muted">A ping is how you find out a drop happened.</p>
        <button className="btn ghost" onClick={() => navigate("/diagnostics")}>
          Check my notifications
        </button>
        {/* The fix when a phone stops getting pings. It used to sit in the home
            feed, where it was one more thing to scroll past every day. */}
        <button
          className="link"
          onClick={async () => {
            if (!window.confirm("Set up notifications again on THIS phone?\n\nYour other devices are not affected.")) return;
            await resetPushSetup(crewId!);
            window.location.reload();
          }}
        >
          Set them up again on this phone
        </button>
      </div>

      <p className="muted center" style={{ fontSize: 12 }}>
        <button className="link" onClick={async () => { await signOut(); window.location.href = "/"; }}>
          Sign out
        </button>
      </p>
    </div>
  );
}
